
import React, { FormEvent, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import AmpButton from "../../components/common/AmpButton/AmpButton";
import AmpCaptcha from "../../components/common/AmpCaptcha/AmpCaptcha";
import AmpFormError from "../../components/common/AmpFormError/AmpFormError";
import AmpInput from "../../components/common/AmpInput/AmpInput";
import AmpLoadingSpinner from "../../components/common/AmpLoadingSpinner/AmpLoadingSpinner";
import SmallHeading from "../../components/common/AmpSmallHeading/AppSmallHeading";
import SuccessOverlay from "../../components/common/SuccessOverlay/SuccessOverlay";
import { resendEmail, verifyEmail } from "../../redux/actions/auth/reg";
import { setLargeToast, setToastNotification } from "../../redux/actions/toastNotification/toastNotification";
import { VERIFY_EMAIL_RESET } from "../../redux/actionTypes/actionsTypes";
import { ResendEmailResponse } from "../../redux/reducers/getAuthReducer/resendVerifyEmailReducer";
import { VerifyEmailResponse } from "../../redux/reducers/getAuthReducer/verifyEmailReducer";
import { RootState } from "../../redux/reducers/root";

import "./VerifyEmail.css";

export default function VerifyEmail(): JSX.Element {

    const { user_profile_id, username } = useParams();
    const { t } = useTranslation("login");
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [resendEnabled, setResendEnabled] = useState(true);
    const [requestSent, setRequestSent] = useState(false);
    const [success, setSuccess] = useState(false);

    const [resendTimeRemaining, setResendTimeRemaining] = useState(0);

    const verifyResult = useSelector((state: RootState) => state.authReducer.verify as VerifyEmailResponse)
    const resendResult = useSelector((state: RootState) => state.authReducer.resend as ResendEmailResponse)

    const [isWrongCode, setError] = useState(false);

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        const data: FormData = new FormData(e.target as HTMLFormElement);
        const values = Object.fromEntries(data.entries()) as Record<string, unknown>;

        const payload = {
            username: username,
            user_profile_id: user_profile_id,
            code: values["code"] as string
        };

        dispatch(verifyEmail(payload));
        setRequestSent(true);
        setError(false);
    }

    useEffect(() => {
        if (requestSent && !verifyResult["loading"] && verifyResult["verified"]) {
            dispatch(setToastNotification(t("acc-verified"), "success"));
            setSuccess(true);
            setTimeout(() => {
                dispatch({
                    type: VERIFY_EMAIL_RESET
                })
                setRequestSent(false);
                navigate("/auth/home/reg-user");
                dispatch(setLargeToast(t("acc-verif-large"), "success", true));
            }, 6000);
        } else if (!verifyResult["loading"] && (requestSent && !verifyResult["verified"])){
            setError(true);
        }
    }, [verifyResult, dispatch, navigate, requestSent, t])

    useEffect( () => {
        if (!resendResult["loading"] && resendResult["sent"]) {
            let timeRemaining = resendTimeRemaining;
            const interval = setInterval(() => {
                if (timeRemaining !== 0){
                    setResendTimeRemaining(timeRemaining - 1);
                } else {
                    setResendEnabled(true);
                    clearInterval(interval);
                }
                timeRemaining--;
            }, 1000);
        }
    }, [resendResult, resendTimeRemaining, dispatch])

    const resend = (e: FormEvent) => {
        e.preventDefault();
        setResendEnabled(false);
        dispatch({type: VERIFY_EMAIL_RESET})
        dispatch(resendEmail(user_profile_id as string, username as string, false))
        dispatch(setToastNotification("Email Sent", "success"));
        setResendTimeRemaining(15);
        setRequestSent(false);
        setError(false);
    }

    return (
        <div className="reset-top">
            <div className="reset-container">
                <div className="reset-header">
                    {t("verify-acc") as string}
                </div>
                <SuccessOverlay success={success}>
                    <AmpLoadingSpinner loading={verifyResult["loading"] || resendResult["loading"]}>
                        <form className="reset-content-top" onSubmit={(e) => { handleSubmit(e) }}>
                            <div className="reset-content">
                                <p>{t('5-email-send-to', { email: username }) as string}</p>
                                <p>{t('5-email-send-no')}</p>
                                <Trans defaults={t("5-contact-us")}>
                                    <Link target="_blank" className="amp-anchor" to="/auth/contact-us" rel="noreferrer noopener"></Link>
                                </Trans>
                                <br />
                                <br />
                                <SmallHeading for={"verify-email"}>
                                    {t("reg-user-uid") as string}
                                </SmallHeading>
                                <AmpInput type="text" name="username" id="verify-email" readOnly={true} value={username} />
                                <SmallHeading for={"verify-code"}>
                                    {t("code") as string}
                                </SmallHeading>
                                <AmpInput type="text" name="code" id="verify-code" />
                                <AmpCaptcha />
                            </div>
                            <AmpFormError errorMessage={t("5-wrong-code")} error={isWrongCode}></AmpFormError>
                            <div className="login-container-footer">
                                <Link target="_blank" rel="noreferrer noopener" className="amp-anchor" to="contact-us">{t("need-help") as string}</Link>
                                <div>
                                    <span>{resendTimeRemaining !== 0 ? resendTimeRemaining : <></>}</span>
                                    <AmpButton type="submit" onClick={(e) => resend(e)} text={t("resend")} disabled={!resendEnabled} />
                                    <AmpButton type="submit" onClick={() => null} text={t("submit")} />
                                </div>
                            </div>
                        </form>
                    </AmpLoadingSpinner>
                </SuccessOverlay>
            </div>
        </div>
    )
}